import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Activity, ArrowRight, Sparkles, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * MetabolicStatusHeroCard — single-sentence status headline at the top of the
 * Dashboard. Plays the role of the "decision engine" primary card.
 *
 * Reads /dashboard/hero and renders one of four visual states:
 *   • onboarding → indigo, "Complete profile" CTA
 *   • fresh      → slate (collecting data / insufficient logs)
 *   • active     → sky
 *   • elevated   → amber/red
 */
const STATUS_CONFIG = {
  onboarding: {
    gradFrom: "#6366F1", gradTo: "#0EA5E9", icon: Sparkles, chip_bg: "Онбординг", chip_en: "Onboarding",
  },
  fresh:      { gradFrom: "#64748B", gradTo: "#475569", icon: Sparkles,        chip_bg: "Събираме данни", chip_en: "Collecting data" },
  active:     { gradFrom: "#0EA5E9", gradTo: "#6366F1", icon: Activity,        chip_bg: "Активна работа", chip_en: "Active" },
  elevated:   { gradFrom: "#F59E0B", gradTo: "#EF4444", icon: AlertTriangle,   chip_bg: "Повишен риск", chip_en: "Elevated" },
};

export default function MetabolicStatusHeroCard({ selectedDate, refreshKey }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";
  const [hero, setHero] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchHero = async () => {
      try {
        // iter265: cookie-based auth — axios withCredentials carries the
        // HttpOnly access_token automatically. 401 is handled by the global
        // interceptor + silent render below.
        const url = selectedDate ? `${API}/dashboard/hero?date=${selectedDate}` : `${API}/dashboard/hero`;
        const { data } = await axios.get(url);
        if (!cancelled) setHero(data);
      } catch { /* silent */ }
    };
    fetchHero();
    // Refresh when meals are added/removed so the "fresh" → "healthy/active" transition is instant.
    const onMealsChanged = () => fetchHero();
    window.addEventListener("meal-log-changed", onMealsChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("meal-log-changed", onMealsChanged);
    };
  }, [API, selectedDate, refreshKey]);

  if (!hero) return null;
  // Legacy "healthy" status → третираме го като "fresh" (зеленото табло е премахнато).
  const statusKey = hero.status_level === "healthy" ? "fresh" : hero.status_level;
  // Сивото "Събираме данни" табло — скриваме го напълно.
  // Вместо него показваме OverviewCards empty state ("Готови сте — време за първо хранене").
  if (statusKey === "fresh") return null;
  const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.fresh;
  const Icon = cfg.icon;
  const ratio = Math.min(2, Math.max(0, hero.dil_ratio || 0));
  const meterPct = Math.min(100, Math.round((ratio / 2) * 100));

  const handleCta = () => {
    const route = hero.cta_route || "/dashboard";
    if (route.startsWith("/")) {
      const [path, hash] = route.split("#");
      navigate(path);
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 180);
      }
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 sm:p-6 text-white shadow-md"
      style={{ backgroundImage: `linear-gradient(135deg, ${cfg.gradFrom}, ${cfg.gradTo})` }}
      data-testid="metabolic-hero-card"
    >
      {/* Subtle grain */}
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 0%, #fff 0.5px, transparent 0.5px), radial-gradient(circle at 80% 100%, #fff 0.5px, transparent 0.5px)", backgroundSize: "14px 14px" }} aria-hidden />

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/15 backdrop-blur-sm flex-shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <span
              className="inline-block text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-full bg-white/15 mb-1.5"
              data-testid="metabolic-hero-chip"
            >
              {bg ? cfg.chip_bg : cfg.chip_en}
            </span>
            <h2 className="text-xl sm:text-2xl font-bold leading-tight" data-testid="metabolic-hero-headline">
              {bg ? hero.headline_bg : hero.headline_en}
            </h2>
            <p className="text-sm opacity-90 mt-1.5 leading-relaxed max-w-2xl" data-testid="metabolic-hero-description">
              {bg ? hero.description_bg : hero.description_en}
            </p>
          </div>
        </div>

        <div className="flex sm:flex-col items-end gap-3 sm:gap-2 shrink-0">
          {hero.status_level !== "onboarding" && hero.status_level !== "fresh" && (
            <div className="flex-1 sm:w-40" data-testid="metabolic-hero-meter">
              <div className="flex justify-between text-[10px] opacity-80 mb-1">
                <span>DIL</span>
                <span>{meterPct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                <div className="h-full rounded-full bg-white/90 transition-all duration-700" style={{ width: `${meterPct}%` }} />
              </div>
            </div>
          )}
          <Button
            size="sm"
            onClick={handleCta}
            className={`h-9 text-xs font-semibold rounded-xl bg-white hover:bg-white/90 ${isDark ? "text-slate-900" : "text-[#1A1A2E]"} shadow-sm`}
            data-testid="metabolic-hero-cta"
          >
            {bg ? hero.cta_label_bg : hero.cta_label_en}
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
